import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import {
  logPipedreamError,
  logPipedreamWarning,
  logSalesforceError,
} from '../logger';
import { SALESFORCE_LOG_EXCEPTIONS } from '@/constants/salesforce';

const PIPEDREAM_TIMEOUT_MS = 290_000;

interface RequestIds {
  submissionId?: string;
  prospectId?: string;
  Id?: string;
}

interface TimedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _startTime?: number;
  _warningTimeouts?: NodeJS.Timeout[];
  _requestIds?: RequestIds;
  _pageUrl?: string;
}

function extractRequestIds(data: unknown): RequestIds {
  try {
    const body = typeof data === 'string' ? JSON.parse(data) : data;
    return {
      submissionId: body?.Submission__c,
      prospectId: body?.Prospect__c,
      Id: body?.Id,
    };
  } catch {
    return {};
  }
}

function createPipedreamInstance(baseURL: string | undefined): AxiosInstance {
  const instance = axios.create({ baseURL, timeout: PIPEDREAM_TIMEOUT_MS });

  instance.interceptors.request.use((config: TimedAxiosRequestConfig) => {
    const url = config.baseURL ?? '';
    config._startTime = Date.now();
    config._requestIds = extractRequestIds(config.data);
    config._warningTimeouts = [1, 2, 3].map((minutes) =>
      setTimeout(
        () => logPipedreamWarning({ url, minutes, ...config._requestIds }),
        minutes * 60_000
      )
    );
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      const timeouts = (response.config as TimedAxiosRequestConfig)
        ._warningTimeouts;
      timeouts?.forEach(clearTimeout);
      return response;
    },
    async (error) => {
      const config = error.config as TimedAxiosRequestConfig;
      config?._warningTimeouts?.forEach(clearTimeout);
      const durationMs = config?._startTime
        ? Date.now() - config._startTime
        : 0;
      const requestBody = (() => {
        try {
          return typeof config?.data === 'string'
            ? JSON.parse(config.data)
            : config?.data;
        } catch {
          return config?.data;
        }
      })();
      await logPipedreamError({
        durationMs,
        errorMessage: error.message ?? 'Unknown error',
        statusCode: error.response?.status,
        url: config?.baseURL,
        requestBody: requestBody ?? null,
        responseBody: error.response?.data ?? null,
        ...config?._requestIds,
      });
      return Promise.reject(error);
    }
  );

  return instance;
}

const REPOPULATION_API_URL = process.env.REPOPULATION_API_URL;
const SUBMISSION_API_URL = process.env.SUBMISSION_API_URL;
const SALESFORCE_API_URL = process.env.SALESFORCE_API_URL;
const PIPEDREAM_API_URL = process.env.PIPEDREAM_SUBMISSION_API_URL;
const TRI_POINT_API_URL = process.env.PIPEDREAM_TRI_POINT_API_URL;
const AGENT_REVIEW_API_URL = process.env.PIPEDREAM_AGENT_REVIEW_API_URL;
const SELF_SERVICE_API_URL = process.env.PIPEDREAM_SELFSERVICE_API_URL;
const CALCULATOR_FORM_API_URL = process.env.PIPEDREAM_CALCULATOR_FORM_API_URL;
const REFERRAL_FORM_API_URL = process.env.PIPEDREAM_REFERRAL_FORM_API_URL;
const CAL_API_URL = process.env.CAL_API_URL;

export const repopulationApi = axios.create({ baseURL: REPOPULATION_API_URL });

export const submissionApi = axios.create({ baseURL: SUBMISSION_API_URL });

export const salesforceApi = axios.create({ baseURL: SALESFORCE_API_URL });

export const pipedreaemApi = createPipedreamInstance(PIPEDREAM_API_URL);

export const agentReviewApi = createPipedreamInstance(AGENT_REVIEW_API_URL);

export const triPointApi = createPipedreamInstance(TRI_POINT_API_URL);

export const selfServiceApi = createPipedreamInstance(SELF_SERVICE_API_URL);

export const calculatorFormApi = createPipedreamInstance(
  CALCULATOR_FORM_API_URL
);

export const referralFormApi = createPipedreamInstance(REFERRAL_FORM_API_URL);

export const calApi = axios.create({ baseURL: CAL_API_URL });

salesforceApi.interceptors.request.use(
  async (config: TimedAxiosRequestConfig) => {
    config._startTime = Date.now();
    try {
      const { headers } = await import('next/headers');
      const headersList = await headers();
      config._pageUrl = headersList.get('referer') ?? undefined;
    } catch {
      // skip
    }
    return config;
  }
);

salesforceApi.interceptors.request.use(async (config) => {
  if (typeof window !== 'undefined') {
    throw new Error(
      'Salesforce requests must run on the server; call SalesforceService instead'
    );
  }
  const { getSalesforceToken } = await import('../tokenManager');
  const token = await getSalesforceToken();
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

salesforceApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      typeof window === 'undefined'
    ) {
      originalRequest._retry = true;

      const { resetToken, getSalesforceToken } = await import(
        '../tokenManager'
      );
      resetToken();
      const newToken = await getSalesforceToken();
      originalRequest.headers.Authorization = `Bearer ${newToken}`;

      return salesforceApi.request(originalRequest);
    }

    return Promise.reject(error);
  }
);

function isSalesforceLogException(error: AxiosError): boolean {
  const responseData = error.response?.data as
    | Record<string, unknown>
    | undefined;

  return SALESFORCE_LOG_EXCEPTIONS.some(
    (ex) =>
      error.config?.url?.includes(ex.url) &&
      error.response?.status === ex.status &&
      responseData === ex.details
  );
}

salesforceApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isSalesforceLogException(error)) {
      return Promise.reject(error);
    }
    const config = error.config as TimedAxiosRequestConfig;
    const durationMs = config?._startTime ? Date.now() - config._startTime : 0;
    const recordId = extractRequestIds(config?.data).Id;
    const requestBody = (() => {
      try {
        return typeof config?.data === 'string'
          ? JSON.parse(config.data)
          : config?.data;
      } catch {
        return config?.data;
      }
    })();
    await logSalesforceError({
      durationMs,
      errorMessage: error.message ?? 'Unknown error',
      statusCode: error.response?.status,
      url: config?.url ?? config?.baseURL,
      requestBody: requestBody ?? null,
      responseBody: error.response?.data,
      ...(recordId ? { recordId } : {}),
      ...(config?._pageUrl ? { pageUrl: config._pageUrl } : {}),
    });
    return Promise.reject(error);
  }
);

calApi.interceptors.request.use((config) => {
  const apiKey = process.env.CAL_API_KEY;
  config.headers.Authorization = `Bearer ${apiKey}`;
  return config;
});
